import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { api } from '@/lib/api';
import { formatCurrency, cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import {
  DollarSign, TrendingUp, Users, Building2, Loader2, BarChart3
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts';

export default function FeeSummary({ orgType } = {}) {
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchFees = useCallback(async () => {
    try {
      const res = await api.get(token, '/analytics/fee-summary');
      setData(res.data);
    } catch (err) {
      console.error('Fee summary error:', err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchFees(); }, [fetchFees]);

  // SFOs have a single family — revenue/fee tracking is irrelevant in v1.
  // Hook calls above always run; this gate just suppresses the render.
  if (orgType === 'sfo') return null;

  if (loading) {
    return (
      <div className="rounded-xl border border-[#E6E0D9] bg-white p-6">
        <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-[#4A3B32]" /></div>
      </div>
    );
  }

  if (!data) return null;

  const { summary, clientFees, bySegment } = data;

  return (
    <div className="rounded-xl border border-[#E6E0D9] bg-white overflow-hidden" data-testid="fee-summary">
      <div className="px-5 pt-5 pb-3">
        <div className="flex items-center gap-2 mb-4">
          <DollarSign className="w-5 h-5 text-[#4A3B32]" />
          <h3 className="font-heading text-base text-[#2C2420]">Revenue & Fees</h3>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
          <div className="rounded-lg bg-[#F5F3F0] p-3 text-center">
            <p className="text-lg font-bold text-[#2C2420]">{formatCurrency(summary.totalRevenue)}</p>
            <p className="text-[10px] text-[#5D524D] uppercase tracking-wider">Total Revenue</p>
          </div>
          <div className="rounded-lg bg-blue-50 p-3 text-center">
            <p className="text-lg font-bold text-blue-700">{formatCurrency(summary.totalRecurring)}</p>
            <p className="text-[10px] text-[#5D524D] uppercase tracking-wider">Recurring</p>
          </div>
          <div className="rounded-lg bg-amber-50 p-3 text-center">
            <p className="text-lg font-bold text-amber-700">{formatCurrency(summary.totalOneOff)}</p>
            <p className="text-[10px] text-[#5D524D] uppercase tracking-wider">One-Off</p>
          </div>
          <div className="rounded-lg bg-green-50 p-3 text-center">
            <p className="text-lg font-bold text-green-700">{formatCurrency(summary.totalAUM)}</p>
            <p className="text-[10px] text-[#5D524D] uppercase tracking-wider">AUM</p>
          </div>
        </div>
      </div>

      {/* Revenue by Segment Chart */}
      {bySegment.length > 0 && (
        <div className="px-5 pb-4">
          <p className="text-xs font-medium text-[#8C8078] uppercase tracking-wider mb-3">Revenue by Segment</p>
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={bySegment} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#E6E0D9" />
                <XAxis type="number" tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} />
                <YAxis dataKey="segment" type="category" width={100} tick={{ fontSize: 11 }} />
                <Tooltip formatter={(val) => formatCurrency(val)} />
                <Bar dataKey="recurring" name="Recurring" fill="#4A3B32" stackId="a" radius={[0, 0, 0, 0]} />
                <Bar dataKey="oneOff" name="One-Off" fill="#C6A88F" stackId="a" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Top Clients by Fees */}
      <div className="px-5 pb-5">
        <p className="text-xs font-medium text-[#8C8078] uppercase tracking-wider mb-3">Top Clients by Revenue</p>
        <div className="space-y-2">
          {clientFees.slice(0, 8).map((cf, i) => (
            <div key={cf.clientId} className="flex items-center justify-between rounded-lg border border-[#E6E0D9] px-3 py-2" data-testid={`fee-client-${i}`}>
              <div className="flex items-center gap-2 min-w-0">
                <span className="w-5 h-5 rounded-full bg-[#4A3B32] text-white text-[10px] flex items-center justify-center shrink-0">{i + 1}</span>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-[#2C2420] truncate">{cf.clientName}</p>
                  <p className="text-[10px] text-[#8C8078]">{cf.segment}</p>
                </div>
              </div>
              <div className="text-right shrink-0">
                <p className="text-sm font-semibold text-[#4A3B32]">{formatCurrency(cf.totalFees)}</p>
                {cf.aum > 0 && <p className="text-[10px] text-[#8C8078]">AUM: {formatCurrency(cf.aum)}</p>}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
